import { useEffect } from "react";

const SITE_NAME = "Sterling Bio";
const SITE_TAGLINE = "UK research peptides";
const DEFAULT_DESC =
  "Sterling Bio supplies UK laboratories with high-purity research peptides and bacteriostatic water. HPLC verified, free UK shipping, same-day dispatch. Strictly not for human or veterinary use.";

const setMeta = (name, content, attr = "name") => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setStructuredData = (id, data) => {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  if (!data) return;
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.id = id;
  s.text = JSON.stringify(data);
  document.head.appendChild(s);
};

/**
 * useSEO — minimal SEO helper that sets <title>, meta description,
 * canonical, Open Graph, Twitter card and optional JSON-LD per page.
 */
export const useSEO = ({
  title,
  description = DEFAULT_DESC,
  path = "",
  image,
  type = "website",
  jsonLd,
} = {}) => {
  useEffect(() => {
    const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — ${SITE_TAGLINE}`;
    document.title = fullTitle;

    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const canonical = `${origin}${path || (typeof window !== "undefined" ? window.location.pathname : "")}`;
    const ogImage =
      image ||
      "https://customer-assets.emergentagent.com/job_uk-peptide-supply/artifacts/mjzekzzr_Screenshot_20260424_194411_ChatGPT.jpg";

    setMeta("description", description);
    setLink("canonical", canonical);

    setMeta("og:site_name", SITE_NAME, "property");
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", type, "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:image", ogImage, "property");
    setMeta("og:locale", "en_GB", "property");

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);

    setStructuredData("ld-page", jsonLd || null);

    return () => setStructuredData("ld-page", null);
  }, [title, description, path, image, type, JSON.stringify(jsonLd)]);
};

export default useSEO;
